import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../api/firebase";
import { addNotification } from "../../../store/notificationSlice";

// Helpers
const getCustomersRef = (tenantId) => collection(db, `tenants/${tenantId}/crm`);

// Helper para dividir arrays en chunks (Firestore Batch Limit = 500)
const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

// --- THUNKS EXISTENTES (Fetch, Delete, Send) SE MANTIENEN IGUAL ---
export const fetchCustomers = createAsyncThunk(
  "crm/fetchCustomers",
  async (tenantId, { rejectWithValue }) => {
    try {
      if (!tenantId) throw new Error("Tenant ID required");
      const q = query(getCustomersRef(tenantId), orderBy("name", "asc"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteCustomer = createAsyncThunk(
  "crm/deleteCustomer",
  async ({ tenantId, customerId }, { dispatch, rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, `tenants/${tenantId}/crm`, customerId));
      dispatch(
        addNotification({ type: "success", message: "Cliente eliminado." }),
      );
      return customerId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const sendCampaign = createAsyncThunk(
  "crm/sendCampaign",
  async (
    { tenantId, newsletter, recipients },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const mailQueueRef = collection(db, "mailQueue");
      await addDoc(mailQueueRef, {
        tenantId,
        createdAt: new Date().toISOString(),
        status: "pending",
        newsletter: {
          subject: newsletter.title,
          body: `Boletín: ${newsletter.title}`,
        },
        recipients: recipients.map((r) => r.email),
      });
      dispatch(
        addNotification({ type: "success", message: "Campaña encolada." }),
      );
      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// --- THUNKS ACTUALIZADOS (Soporte Billing) ---

export const addCustomer = createAsyncThunk(
  "crm/addCustomer",
  async ({ tenantId, customerData }, { dispatch, rejectWithValue }) => {
    try {
      // Inyectamos metadatos y estructura Billing
      const enhancedData = {
        ...customerData,
        // Aseguramos que billing exista aunque sea null para consistencia futura
        billing: customerData.billing || null,
        createdAt: new Date().toISOString(),
        isActive: true,
        source: "manual",
      };
      const docRef = await addDoc(getCustomersRef(tenantId), enhancedData);
      dispatch(
        addNotification({ type: "success", message: "Cliente añadido." }),
      );
      return { id: docRef.id, ...enhancedData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateCustomer = createAsyncThunk(
  "crm/updateCustomer",
  async (
    { tenantId, customerId, customerData },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const docRef = doc(db, `tenants/${tenantId}/crm`, customerId);
      const updatePayload = {
        ...customerData,
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(docRef, updatePayload);
      dispatch(
        addNotification({ type: "success", message: "Cliente actualizado." }),
      );
      return { id: customerId, ...updatePayload };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// --- THUNK OPTIMIZADO: IMPORTACIÓN MASIVA CON CHUNKING ---
export const importCustomers = createAsyncThunk(
  "crm/importCustomers",
  async ({ customers, log, tenantId }, { dispatch, rejectWithValue }) => {
    try {
      if (!tenantId) throw new Error("Tenant ID missing for import.");

      const timestamp = new Date().toISOString();

      // 1. Preparar Payload Base
      const preparedCustomers = customers.map((customer) => {
        // Desestructuración defensiva
        const {
          nombre,
          identificacion,
          dv,
          correo,
          telefono,
          segmento,
          subsegmento,
          empresa,
          tipo,
          // Nuevos campos de Excel (Billing)
          codigo_cliente,
          rut,
          responsable_iva,
          moneda,
          forma_pago,
          direccion_linea1,
          direccion_ciudad,
          direccion_pais,
          logo_url,
        } = customer;

        // Construcción del objeto Billing
        const billingData = {
          codigoCliente: codigo_cliente || null,
          rut: rut || null,
          sujetoIVA: responsable_iva === "SI" || responsable_iva === true,
          divisa: moneda || "COP",
          formaPago: forma_pago || null,
          logoUrl: logo_url || null,
          direccion: {
            linea1: direccion_linea1 || null,
            ciudad: direccion_ciudad || null,
            codigoPostal: null, // No mapeado en excel simple
            pais: direccion_pais || null,
          },
        };

        return {
          // Core
          name: nombre,
          identificacion: String(identificacion),
          dv: dv ? String(dv) : "",
          email: correo,
          phone: telefono ? String(telefono) : "",
          segment: segmento || "General",
          company: empresa || "",

          // Enterprise Fields
          subSegment: subsegmento || "",
          customerType: tipo || "B2C",
          billing: billingData, // Nuevo Objeto

          isActive: true,
          source: "import",
          createdAt: timestamp,
          updatedAt: timestamp,
          importLogId: log.timestamp,
        };
      });

      // 2. Procesamiento por Lotes (Chunking)
      // Firestore limita writeBatch a 500 operaciones. Usamos 450 por seguridad.
      const BATCH_SIZE = 450;
      const chunks = chunkArray(preparedCustomers, BATCH_SIZE);
      let processedCount = 0;

      for (const chunk of chunks) {
        const batch = writeBatch(db);

        chunk.forEach((customerData) => {
          const newCustomerRef = doc(collection(db, `tenants/${tenantId}/crm`));
          batch.set(newCustomerRef, customerData);
        });

        // Commit del chunk actual
        await batch.commit();
        processedCount += chunk.length;
        console.log(`[Import] Procesado lote de ${chunk.length} registros.`);
      }

      // 3. Registro de Auditoría (Operación separada para evitar desbordar el último batch)
      try {
        await addDoc(collection(db, "audit_logs"), {
          ...log,
          tenantId,
          type: "crm_import_v2",
          chunks: chunks.length,
        });
      } catch (logErr) {
        console.warn(
          "No se pudo guardar el log de auditoría, pero los datos se importaron.",
          logErr,
        );
      }

      dispatch(
        addNotification({
          type: "success",
          message: `${processedCount} clientes importados correctamente en ${chunks.length} lotes.`,
        }),
      );

      // Recargar clientes
      dispatch(fetchCustomers(tenantId));

      return true;
    } catch (error) {
      return rejectWithValue(`Import Error: ${error.message}`);
    }
  },
);

const crmSlice = createSlice({
  name: "crm",
  initialState: { customers: [], loading: false, error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addCustomer.fulfilled, (state, action) => {
        state.customers.push(action.payload);
        state.customers.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        const index = state.customers.findIndex(
          (c) => c.id === action.payload.id,
        );
        if (index !== -1) state.customers[index] = action.payload;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.customers = state.customers.filter(
          (c) => c.id !== action.payload,
        );
      })
      .addCase(importCustomers.pending, (state) => {
        state.loading = true;
      })
      .addCase(importCustomers.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(importCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default crmSlice.reducer;
